import type { TParams } from './types.ts';

/**
 * Render a template with `{name}` placeholders against a params object.
 *
 * Rules
 * - Unknown placeholders stay literal (`{foo}`) — surfaces missing data
 *   loudly in the UI instead of eating copy.
 * - Unused params are silently ignored; the translator might not need
 *   every datum provided by the caller in every locale.
 * - No escape sequences. Double braces are NOT honoured: `{{name}}`
 *   with `{ name: 'x' }` renders `{x}`, because the inner `{name}` is
 *   still a valid placeholder and gets substituted while the outer
 *   `{` passes through verbatim. Dictionary authors must therefore
 *   avoid literal braces in templates. If a future key genuinely needs
 *   one, introduce an escape protocol then and update this comment.
 *
 * This is intentionally ~10 lines — the whole i18n runtime is built to
 * stay under the "minimal implementation" bar in the global harness
 * rather than pull in `intl-messageformat` or `i18next`.
 */
const PLACEHOLDER_PATTERN = /\{(\w+)\}/g;

export function substitute(template: string, params: TParams | undefined): string {
  if (params === undefined) {
    return template;
  }
  return template.replace(PLACEHOLDER_PATTERN, (match, rawKey: string) => {
    const value = params[rawKey];
    if (value === undefined) {
      // Keep the original `{key}` so missing data is visible in QA /
      // staging; a template that silently swallows missing params is
      // much harder to debug than one that screams.
      return match;
    }
    return String(value);
  });
}
